import React from 'react';
import PropTypes from 'prop-types';
import { View, StyleSheet } from 'react-native';
import { FloatingLabelInput } from 'react-native-floating-label-input';
import { TouchableOpacity } from 'react-native-gesture-handler';

import Colors from '../../constants/Colors';

import { AppText } from '../AppText';

/**
 * `MyFloatingLabelInput` is a functional component that renders an input field with a floating label.
 * The label's color changes based on whether the input is required or disabled.
 * It also shows an error message if provided.
 *
 * @component
 * @param {Object} props - Props for the MyFloatingLabelInput component.
 * @param {string} props.label - The label for the input field.
 * @param {string} props.value - The current value of the input field.
 * @param {Function} props.onChangeText - Callback for text change events.
 * @param {Function} [props.onSubmitEditing] - Callback for when the user submits their input.
 * @param {number|string} [props.width] - The width of the input field, can be a number or a percentage as a string.
 * @param {boolean} [props.isPassword] - Determines if the input is for a password (enables toggle for visibility).
 * @param {boolean} [props.togglePassword] - State to control the visibility of the password.
 * @param {string|boolean} [props.error] - Error message to display (if string) or simply indicate an error state (if boolean).
 * @param {string} [props.keyboardType] - Type of keyboard to display for input.
 * @param {boolean} [props.disabled] - Indicates if the input field is disabled.
 * @param {boolean} [props.isRequired] - Indicates if the field is required.
 * @param {Object} [props.translation] - Object containing translation strings.
 * @param {string} [props.testID] - Test ID for the input
 *
 * @returns {React.ReactElement} A React Element representing the custom floating label input field.
 */
function MyFloatingLabelInput(props) {
  const {
    label,
    value,
    onChangeText,
    onSubmitEditing,
    width,
    isPassword,
    togglePassword,
    error,
    keyboardType,
    disabled,
    isRequired,
    translation,
    testID,
    id,
  } = props;

  const getlabelColor = () => {
    if (disabled) {
      return Colors.lightGray;
    }
    if (isRequired) {
      return Colors.brightRed;
    }
    return Colors.veryDarkGray;
  };

  return (
    <View style={styles.view}>
      <View
        style={{
          width: width || 100,
        }}
      >
        <AppText
          style={{
            color: getlabelColor(),
          }}
        >
          {isRequired ? `${label}*` : label}
        </AppText>
        <TouchableOpacity testID={testID}>
          <FloatingLabelInput
            testID={testID}
            id={id}
            value={value}
            onChangeText={onChangeText}
            returnKeyType="done"
            onSubmitEditing={onSubmitEditing || null}
            containerStyles={{
              borderWidth: 0,
              borderBottomRightRadius: 0,
              borderBottomLeftRadius: 0,
              width: width || 100,
              overflow: 'hidden',
              marginTop: 8,
            }}
            inputStyles={{
              color: Colors.veryDarkGray,
              fontSize: 16,
              fontFamily: 'roboto-regular',
              borderBottomWidth: 1,
              paddingBottom: 4,
              borderBottomColor: error ? Colors.brightRed : Colors.grayBorder,
            }}
            isPassword={isPassword}
            togglePassword={togglePassword}
            keyboardType={keyboardType || 'default'}
            disabled={disabled || false}
          />
        </TouchableOpacity>
      </View>
      <View style={styles.errorContainer}>
        {error && typeof error === 'string' ? (
          <AppText
            style={{
              fontSize: 12,
              color: Colors.brightRed,
              fontWeight: 'bold',
              maxWidth: width || 100,
            }}
          >
            {error}
          </AppText>
        ) : null}
      </View>
      {isRequired && (
        <View style={styles.requiredContiner}>
          <AppText style={[styles.requiredTextStyle, styles.requiredHelperStyle]}>
            {translation.form_required}
          </AppText>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  view: {
    borderWidth: 0,
    marginBottom: 20,
  },
  errorContainer: {
    flexDirection: 'row',
  },
  requiredContiner: {
    flexDirection: 'row',
  },
  requiredTextStyle: {
    color: Colors.brightRed,
    fontWeight: 'bold',
  },
  requiredHelperStyle: {
    fontSize: 10,
  },
});

MyFloatingLabelInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChangeText: PropTypes.func.isRequired,
  onSubmitEditing: PropTypes.func,
  width: PropTypes.any, // number or string, %
  error: PropTypes.any, // sometime boolean
  isPassword: PropTypes.bool,
  togglePassword: PropTypes.bool,
  keyboardType: PropTypes.string,
  disabled: PropTypes.bool,
  testID: PropTypes.string,
  id: PropTypes.string,
};

export default MyFloatingLabelInput;
